import { useEffect, useState } from 'react';
import Select from 'react-select';
import useTechnologyOptions from '../../../Helpers/CustomHooks/useTechnologyOptions';
import useCategoryOptions from '../../../Helpers/CustomHooks/useCategoryOptions';
import useIssuesOptions from '../../../Helpers/CustomHooks/useIssuesOptions';

export type DDLOption = { text: string; value: number };

export type ReassignSelection = {
    disciplineId: number | null;
    technologyId: number | null;
    categoryId: number | null;
    issueId: number | null;
};

// react-select's style functions don't get props from us, so we read an
// `aria-invalid="true"` flag off the rendered control via the selectProps.
const buildSelectStyles = (hasError: boolean) => ({
    control: (provided, state) => ({
        ...provided,
        background: state.isDisabled ? '#f3f4f6' : 'white',
        borderColor: hasError && !state.isDisabled
            ? '#ef4444'
            : state.isDisabled ? '#d1d5db' : '#fdb419',
        borderWidth: 1,
        boxShadow: state.isFocused && !state.isDisabled
            ? `0 0 0 1px ${hasError ? '#ef4444' : '#fdb419'}`
            : 'none',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        opacity: state.isDisabled ? 0.6 : 1,
        width: '100%',
        minWidth: '200px',
        '@media (prefers-color-scheme: dark)': {
            background: state.isDisabled ? '#3f3f3f' : '#6e6e6e',
            borderColor: hasError && !state.isDisabled
                ? '#ef4444'
                : state.isDisabled ? '#4b5563' : '#6e6e6e',
        },
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#fdb419' : '#ffffff',
        color: state.isSelected ? '#ffffff' : '#000000',
        ':hover': { backgroundColor: '#fdb419', color: '#ffffff' },
        '@media (prefers-color-scheme: dark)': {
            backgroundColor: state.isSelected ? '#fdb419' : '#6e6e6e',
            color: '#ffffff',
            ':hover': { backgroundColor: '#fdb419', color: '#ffffff' },
        },
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.isDisabled ? '#9ca3af' : 'inherit',
    }),
    placeholder: (provided, state) => ({
        ...provided,
        color: state.isDisabled ? '#9ca3af' : '#6b7280',
        fontStyle: state.isDisabled ? 'italic' : 'normal',
    }),
    dropdownIndicator: (base, state) => ({
        ...base,
        color: state.isDisabled ? '#9ca3af' : (hasError ? '#ef4444' : '#fdb419'),
    }),
    indicatorSeparator: () => ({ display: 'none' }),
});

const enabledStyles = buildSelectStyles(false);
const errorStyles = buildSelectStyles(true);

type CascadingSelectProps = {
    label: string;
    required?: boolean;
    dataTestId: string;
    value: number | null;
    options: DDLOption[];
    onChange: (next: number | null) => void;
    isDisabled?: boolean;
    disabledPlaceholder?: string;
    activePlaceholder?: string;
    error?: string | null;
};

function CascadingSelect({
    label,
    required = false,
    dataTestId,
    value,
    options,
    onChange,
    isDisabled = false,
    disabledPlaceholder,
    activePlaceholder,
    error = null,
}: CascadingSelectProps) {
    const selected = value == null ? null : options.find(o => o.value === value) ?? null;
    const showError = Boolean(error) && !isDisabled;
    return (
        <div className="flex flex-col md:flex-row items-start md:items-center mb-3">
            <label className="text-grey-darkest block text-left mb-1 md:mb-0 md:w-1/3">
                {label}
                {required && (
                    <span className="text-red-500 px-2 text-base" aria-hidden="true">*</span>
                )}
            </label>
            <div className="w-full md:w-[320px]" data-testid={dataTestId}>
                <Select
                    styles={showError ? errorStyles : enabledStyles}
                    options={options}
                    value={selected}
                    onChange={(opt) => onChange((opt as DDLOption | null)?.value ?? null)}
                    getOptionLabel={(o) => (o as DDLOption).text}
                    getOptionValue={(o) => String((o as DDLOption).value)}
                    isClearable={!required}
                    isDisabled={isDisabled}
                    aria-invalid={showError || undefined}
                    placeholder={isDisabled ? (disabledPlaceholder ?? `Select ${label.toLowerCase()}`) : (activePlaceholder ?? `Select ${label.toLowerCase()}`)}
                />
                {showError && (
                    <p className="text-red-500 text-xs mt-1" data-testid={`${dataTestId}-error`}>{error}</p>
                )}
            </div>
        </div>
    );
}

type Props = {
    initial: ReassignSelection;
    disciplineOptions: DDLOption[];
    onChange: (selection: ReassignSelection) => void;
    // The parent populates this ref so it can flip the form into "show errors"
    // mode after a failed submit attempt. Stays null until the user clicks YES
    // on the modal with an incomplete selection.
    triggerValidationRef?: React.MutableRefObject<(() => void) | null>;
};

export default function ReassignDisciplineForm({ initial, disciplineOptions, onChange, triggerValidationRef }: Props) {
    const [disciplineId, setDisciplineId] = useState<number | null>(initial.disciplineId);
    const [technologyId, setTechnologyId] = useState<number | null>(initial.technologyId);
    const [categoryId, setCategoryId] = useState<number | null>(initial.categoryId);
    const [issueId, setIssueId] = useState<number | null>(initial.issueId);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const technologyOptions = useTechnologyOptions(Number(disciplineId)) as DDLOption[];
    const categoryOptions = useCategoryOptions(Number(disciplineId), Number(technologyId)) as DDLOption[];
    const issueOptions = useIssuesOptions(Number(disciplineId)) as DDLOption[];

    // Drop a child selection if it is no longer valid for the current parent context
    // (e.g. discipline-scoped option list changed, or options finished loading later).
    useEffect(() => {
        if (technologyId != null && !technologyOptions.some(o => o.value === technologyId)) {
            setTechnologyId(null);
        }
    }, [technologyOptions, technologyId]);

    useEffect(() => {
        if (categoryId != null && !categoryOptions.some(o => o.value === categoryId)) {
            setCategoryId(null);
        }
    }, [categoryOptions, categoryId]);

    useEffect(() => {
        if (issueId != null && !issueOptions.some(o => o.value === issueId)) {
            setIssueId(null);
        }
    }, [issueOptions, issueId]);

    // Bridge selection back to the parent (which holds the ref the modal's confirm
    // handler will read). onChange is intentionally not in deps: callers pass a
    // fresh closure each render and including it would cause an infinite loop.
    useEffect(() => {
        onChange({ disciplineId, technologyId, categoryId, issueId });
    }, [disciplineId, technologyId, categoryId, issueId]);

    // Expose a "flip to error mode" trigger so the parent's submit handler can
    // turn on the inline error UI when validation fails.
    useEffect(() => {
        if (!triggerValidationRef) return;
        triggerValidationRef.current = () => setSubmitAttempted(true);
        return () => { triggerValidationRef.current = null; };
    }, [triggerValidationRef]);

    const handleDisciplineChange = (next: number | null) => {
        setDisciplineId(next);
        // Cascade reset: parent change invalidates all downstream selections.
        setTechnologyId(null);
        setCategoryId(null);
        setIssueId(null);
    };

    const handleTechnologyChange = (next: number | null) => {
        setTechnologyId(next);
        setCategoryId(null);
    };

    // Per-field validation. Errors are only surfaced after a failed submit
    // attempt — the form opens clean even if some fields start empty. Each rule
    // is also gated on its prerequisite parent being set, so we never tell the
    // user a field is "Required" before they can reach it.
    const REQUIRED = 'Required';
    const disciplineError = submitAttempted && disciplineId == null ? REQUIRED : null;
    const technologyError = submitAttempted && disciplineId != null && technologyId == null ? REQUIRED : null;
    const categoryError = submitAttempted && disciplineId != null && technologyId != null && categoryId == null ? REQUIRED : null;
    const issueError = submitAttempted && disciplineId != null && issueId == null ? REQUIRED : null;

    return (
        <div data-testid="reassign-discipline-form" className="min-w-[280px] md:min-w-[520px]">
            <CascadingSelect
                label="Assigned Discipline"
                required
                dataTestId="reassign-discipline-dropdown-input"
                value={disciplineId}
                options={disciplineOptions}
                onChange={handleDisciplineChange}
                error={disciplineError}
            />
            <CascadingSelect
                label="Technology"
                required
                dataTestId="reassign-technology-dropdown-input"
                value={technologyId}
                options={technologyOptions}
                onChange={handleTechnologyChange}
                isDisabled={!disciplineId}
                disabledPlaceholder="Select discipline first"
                error={technologyError}
            />
            <CascadingSelect
                label="Category"
                required
                dataTestId="reassign-category-dropdown-input"
                value={categoryId}
                options={categoryOptions}
                onChange={setCategoryId}
                isDisabled={!disciplineId || !technologyId}
                disabledPlaceholder={!disciplineId ? 'Select discipline first' : 'Select technology first'}
                error={categoryError}
            />
            <CascadingSelect
                label="Issue"
                required
                dataTestId="reassign-issue-dropdown-input"
                value={issueId}
                options={issueOptions}
                onChange={setIssueId}
                isDisabled={!disciplineId}
                disabledPlaceholder="Select discipline first"
                error={issueError}
            />
        </div>
    );
}

// Top-level validity check used by the parent before submitting.
export function isReassignSelectionComplete(s: ReassignSelection): boolean {
    return s.disciplineId != null && s.technologyId != null && s.categoryId != null && s.issueId != null;
}
